import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnInit,
  Signal,
  ViewChild,
  WritableSignal,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { YouTubePlayer } from '@angular/youtube-player';
import { PopupPositionEnum } from '@app/core/enums/popup-view-type.enum';
import { MultimediaType } from '@app/core/enums/widget/multimedia-type.enum';
import { ViewType } from '@app/core/enums/widget/view-type.enum';
import { CarouselService } from '@app/core/services/carousel/carousel.service';
import { BaseWidget } from '../base-widget/base-widget';
import { StringUtils } from '@app/core/utils/string.utils';
import {
  getReviewTextForPreview,
  isReviewExpandedForPreview,
  shouldShowReviewToggleForPreview,
  toggleReviewTextStateForPreview,
} from '../utils/review-media.util';

@Component({
  selector: 'multimedia',
  templateUrl: './multimedia.component.html',
  styleUrl: './multimedia.component.scss',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, YouTubePlayer],
  providers: [CarouselService],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class MultimediaComponent extends BaseWidget implements OnInit {
  @HostBinding('class') @Input() ngClass: any;
  @HostBinding('style') @Input() ngStyle: any;

  @Input() actionEvent: (event: any) => void;
  slot: any = input();
  slotData: any = signal(null);
  skeleton: any;
  img: HTMLImageElement;
  imageIndex: any;
  swiperLoaded = signal(false);
  ViewType = ViewType;
  isPopup: WritableSignal<boolean> = signal(false);
  activeIndexView: WritableSignal<number> = signal(0);
  showLoadingBar: WritableSignal<boolean> = signal(false);
  reviewPreviewCharLimit: WritableSignal<number> = signal(0);
  private expandedReviewState: Record<string, boolean> = {};
  isOriginalGridSlot: WritableSignal<boolean> = signal(false); // Internal state for navigation (back button)

  reviewContextEnabled: Signal<boolean> = computed(() => {
    const slot = this.slotData();
    return Boolean(slot?.widget?.data?.reviewContext);
  });

  currentReviewDetails: Signal<any> = computed(() => {
    if (!this.reviewContextEnabled()) {
      return null;
    }
    const slot = this.slotData();
    const index = this.activeIndexView();
    return (
      slot?.widget?.data?.multimediaComponents?.[index]?.value?.reviewData ??
      null
    );
  });

  gridViewOnly: Signal<boolean> = computed(
    () => this.slotData()?.widget?.data?.gridViewOnly ?? false,
  );
  showThumbnails: Signal<boolean> = computed(
    () => this.slotData()?.widget?.data?.showThumbnails ?? false,
  );
  multimediaComponents: Signal<any[]> = computed(
    () => this.slotData()?.widget?.data?.multimediaComponents ?? [],
  );

  MultimediaType = MultimediaType;

  private readonly _mCarouselService = inject(CarouselService);
  private dialogRef?: MatDialogRef<MultimediaComponent> = inject(MatDialogRef, {
    optional: true,
  });

  offerIcon = signal(
    'https://static3.industrybuying.com/a-wk/images/svg/offer-icon.svg',
  );
  shipWithinIcon = signal(
    'https://static3.industrybuying.com/a-wk/images/webp/delivery-vehicle.webp',
  );

  closePopupIcon = signal(
    'https://static3.industrybuying.com/a-wk/images/svg/popup-close.svg',
  );

  @ViewChild('swiper', { static: false })
  swiper?: ElementRef<any>;

  protected readonly data?: {
    slot: any;
    viewType: string;
    selected: number;
    action: any;
  } = inject(MAT_DIALOG_DATA, { optional: true });
  StringUtils = StringUtils;
  ngOnInit(): void {}

  ngAfterViewInit(): void {
    if (this.data?.viewType === 'POPUP') {
      this.slotData.set(this.data.slot);
      this.isPopup.set(true);
    } else {
      this.slotData.set(this.slot());
    }
    this._registerSwiper();
    this.skeleton = this.slotData()?.widget?.data?.multimediaComponents[0];
    this.reviewPreviewCharLimit.set(
      this.slotData()?.widget?.data?.reviewPreviewCharLimit,
    );
  }

  private async _registerSwiper() {
    await this._mCarouselService.loadCarousel();
    this.swiperLoaded.set(true);
  }

  onSwiperInit(event: any) {
    setTimeout(() => {
      this.swiper?.nativeElement?.swiper?.keyboard?.disable();
      if (this.data?.selected >= 0) {
        this.slideChange(this.data?.selected);
      }
      if (!this.isPopup()) {
        this.swiper?.nativeElement?.swiper?.navigation?.disable();
      }
    });
  }

  slideChange(i: number) {
    this.activeIndexView.set(i);
    this.swiper?.nativeElement?.swiper?.slideTo(i);
  }

  showAllPhotosModal(event: any, data: any, selectedIndex: number) {
    this._stopPropagation(event);
    if (this.data?.viewType === 'POPUP') return;
    const popupData = data?.action?.params;
    const viewType = this.slotData()?.widget?.data?.viewType;
    const dynamicPopupData = {
      data: {
        slot: this.slotData(),
        viewType: 'POPUP',
        selected: selectedIndex,
      },
      ...(viewType === PopupPositionEnum.BOTTOM ? null : popupData.layout),
    };
    this.showPopup(dynamicPopupData, popupData.popupName, viewType);
  }

  onSwiperSlideChange(event) {
    this.activeIndexView.set(event?.detail?.[0]?.activeIndex);
  }

  popupClose() {
    this.showLoadingBar.set(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.dialogRef?.close();
      }, 100);
    });
  }

  private updateSlotData(updates: { gridViewOnly?: boolean }) {
    this.slotData.update((current) => ({
      ...current,
      widget: {
        ...current?.widget,
        data: {
          ...current?.widget?.data,
          ...updates,
        },
      },
    }));
  }

  launchMultimediaPopup(index: number) {
    if (this.data) {
      this.data.selected = index;
    }
    this.updateSlotData({ gridViewOnly: false });
    this.isOriginalGridSlot.set(true);
  }

  backToUserImagesGrid() {
    this.updateSlotData({ gridViewOnly: true });
    this.isOriginalGridSlot.set(false);
  }

  getReviewText(text: string | undefined, key: number): string {
    return getReviewTextForPreview(
      text,
      key,
      this.reviewPreviewCharLimit(),
      this.expandedReviewState,
    );
  }

  shouldShowReviewToggle(text?: string): boolean {
    return shouldShowReviewToggleForPreview(
      text,
      this.reviewPreviewCharLimit(),
    );
  }

  isReviewExpanded(key: number): boolean {
    return isReviewExpandedForPreview(key, this.expandedReviewState);
  }

  toggleReviewText(key: number, event?: Event) {
    toggleReviewTextStateForPreview(key, this.expandedReviewState);
  }
}
